import cn from 'classnames'

import Icon from 'components/uiKit/Icon'
import type { IKitControlProps } from 'components/uiKit/KitTypes'
import { KitSize } from 'components/uiKit/KitTypes'
import { isMixed } from 'components/uiKit/KitUtils'
import { testProps, testPropsEl } from 'utils/test/qaData'

import * as s from './CheckableTag.module.scss'

export interface ICheckableTagProps<T> extends IKitControlProps<T[]> {
  options: { value: T; label: string }[]
  mode?: 'single' | 'multiple'
  hideClose?: boolean
  postfix?: React.ReactNode
}

const CheckableTag = <T extends string | null>({
  options,
  onChange,
  value: rawValue,
  name,
  disabled,
  mode = 'multiple',
  hideClose = false,
  postfix,
}: ICheckableTagProps<T>) => {
  const value = isMixed(rawValue) ? ([] as T[]) : (rawValue ?? ([] as T[]))
  const onTagClick = (tag: T) => {
    if (disabled) {
      return
    }

    const alreadyChecked = value?.includes(tag)
    let newValue: T[] = []

    if (mode === 'single') {
      newValue = !hideClose && alreadyChecked ? [] : [tag]
    } else if (mode === 'multiple') {
      newValue = alreadyChecked ? value.filter((t) => t !== tag) : [...value, tag]
    }
    onChange?.(newValue)
  }

  return (
    <div
      className={s.root}
      {...testProps({ el: 'checkableTags', name, value: value ? value.join(',') : null })}
    >
      {options.map(({ value: tagValue, label }) => {
        const tagCn = cn(s.tag, {
          [s.checked]: value?.includes(tagValue),
          [s.disabled]: disabled,
        })
        return (
          <div
            className={tagCn}
            key={tagValue}
            onClick={() => onTagClick(tagValue)}
            {...testPropsEl('checkableTag', { name, value: tagValue }, { label, disabled })}
          >
            {label}
          </div>
        )
      })}
      {postfix && postfix}
      {value && !!value.length && !disabled && !hideClose && (
        <div
          className={s.clearButton}
          onClick={() => onChange?.([])}
          {...testProps({ el: 'checkableTag', name: 'clearTags' })}
        >
          <Icon name='otherClose' size={KitSize.M} />
        </div>
      )}
    </div>
  )
}

export default CheckableTag
